import { useCallback, useRef } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { legacy } from '@/shared/api/client';

export interface CampaignReportTotals {
  dispatches: number;
  /** Pessoas únicas que os disparos tentaram alcançar. */
  audience: number;
  /** Pessoas únicas com entrega confirmada. */
  recipients: number;
  /** `recipients` ÷ `audience`, em %. */
  reachRate: number;
  /** Por pessoa em cada disparo (app + carteira da mesma pessoa contam 1), somado nos disparos. */
  sent: number;
  delivered: number;
  failed: number;
  deliveryRate: number;
  /** Pessoas únicas que compraram dentro da janela de performance. */
  buyers: number;
  conversionRate: number;
  /** Compras atribuídas, sem as estornadas. */
  orders: number;
  /** Pedidos com valor no cupom (`moneyValorCompra`); só eles entram na receita e no ticket. */
  ordersWithValue: number;
  /** Compras atribuídas que foram estornadas (fora de `orders`). */
  reversedOrders: number;
  revenue: number;
  avgTicket: number;
}

export interface CampaignReportDispatch {
  id: string;
  date: string;
  status: 'running' | 'success' | 'error';
  error: string | null;
  tipo_disparo: string | null;
  sent: number;
  delivered: number;
  failed: number;
}

export interface CampaignReportDay {
  date: string;
  sent: number;
  delivered: number;
  failed: number;
  orders: number;
  revenue: number;
}

export interface CampaignReportBuyer {
  userId: string;
  name: string;
  idPassbook: string;
  orders: number;
  revenue: number;
  receivedAt: string;
  firstPurchaseAt: string;
  lastPurchaseAt: string;
  /** Semente do avatar, como em Leads. A API anterior a este campo não manda: o avatar cai no `userId`. */
  uniqueId?: string;
  gender?: string;
}

export interface CampaignReport {
  campaign: {
    id: string;
    nome?: string;
    mensagem?: string;
    plataforma?: string;
    tipo_disparo?: string;
    ativo?: boolean;
    id_passbook: string;
  };
  /** Dias após o disparo em que uma compra ainda conta (`performance` da campanha). */
  windowDays: number;
  period: { from: string; to: string; days: number };
  generatedAt: string;
  totals: CampaignReportTotals;
  dispatches: CampaignReportDispatch[];
  daily: CampaignReportDay[];
  /** Compras por dias desde o disparo (0 = no mesmo dia). */
  purchaseLag: Array<{ day: number; orders: number; buyers: number }>;
  buyers: CampaignReportBuyer[];
  buyersTruncated: boolean;
}

export const campaignReportKeys = {
  detail: (idPassbook: string, campaignId: string, days: number) =>
    ['campaign-report', idPassbook, campaignId, days] as const,
};

/**
 * Report da campanha direto do banco (GET /campaign/:id/report no legado).
 * `refresh` refaz a conta na API, que guarda o resultado por 5 minutos.
 */
export function useCampaignReport(idPassbook: string, campaignId: string | undefined, days: number, enabled = true) {
  /** Consumido uma única vez pelo próximo fetch — fura o cache da API. */
  const forceRefresh = useRef(false);
  const query = useQuery({
    queryKey: campaignReportKeys.detail(idPassbook, campaignId ?? '', days),
    queryFn: () => {
      const refresh = forceRefresh.current;
      forceRefresh.current = false;
      return legacy.get<CampaignReport>(`/campaign/${campaignId}/report?days=${days}${refresh ? '&refresh=1' : ''}`, {
        headers: { 'X-Passbook-Id': idPassbook },
      });
    },
    enabled: enabled && Boolean(idPassbook && campaignId),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  const { refetch } = query;
  const refresh = useCallback(() => {
    forceRefresh.current = true;
    return refetch();
  }, [refetch]);

  return { ...query, refresh };
}
